// Local fixtures for the leader feature, used when VITE_USE_MOCKS routes handleRequest to the mock branch.

import { getMean } from '../../../../../pure-js/libs/Common'
import {
  LEADER_INSIGHTS_MIN_PEOPLE,
  buildLeaderDocId,
  buildScoreSpread,
  joinLeadershipNote,
  roundScore,
  type LeaderScoreZoneFloors
} from '../../../../../pure-js/libs/leaderInsights'
import {
  LeaderCollaborationLevel,
  LeaderHighlightTone,
  LeaderInsightsStatus,
  LeaderMetricDirection,
  type LeaderHighlight,
  type LeaderInsightsResult,
  type LeaderInsightsView,
  type LeaderMetricSuggestion,
  type LeaderPersonView,
  type LeaderScope,
  type LeaderScopeMember
} from '../../../../../pure-js/types/Leader'

const nowIso = () => new Date().toISOString()

const MOCK_ORGANIZATION_ID = 'mock-organization'
const MOCK_LEADER_USER_ID = 'me'

const MOCK_TEAM_NORTH_NAME = 'Team North'
const MOCK_TEAM_SOUTH_NAME = 'Team South'
const MOCK_TEAM_NORTH_SIZE = 5

export const MOCK_LEADER_PEOPLE: LeaderPersonView[] = [
  'Alva Lindqvist',
  'Bashir Rahimi',
  'Carla Moreno',
  'Dmitri Volkov',
  'Elsa Bergström',
  'Farah Haddad',
  'Gustav Nyberg',
  'Hanna Sjöberg',
  'Ibrahim Osei'
].map((displayName, index) => ({
  displayName,
  teamNames: [index < MOCK_TEAM_NORTH_SIZE ? MOCK_TEAM_NORTH_NAME : MOCK_TEAM_SOUTH_NAME]
}))

// Nobody's own score leaves the server; here they only make the spread the page receives.
const MOCK_PSYCHOLOGICAL_SAFETY_SCORES: number[] = [0.42, 0.55, 0.61, 0.64, 0.7, 0.73, 0.78, 0.84, 0.91]

const MOCK_LEADERSHIP_HIGHLIGHTS: LeaderHighlight[] = [
  {
    tone: LeaderHighlightTone.STRENGTH,
    title: 'Quick to help',
    detail: 'Your people help each other readily and share what they know.'
  },
  {
    tone: LeaderHighlightTone.STRENGTH,
    title: 'Mostly safe to speak up',
    detail: 'Most of them feel safe raising a concern, even when the plan changes.'
  },
  {
    tone: LeaderHighlightTone.ATTENTION,
    title: 'Asking for help late',
    detail: 'Two of your teams wait a long time before saying they are stuck.'
  }
]

const MOCK_LEADERSHIP_SUGGESTION =
  'In your next check-in, ask each person “Is anything blocking you right now?” before discussing progress.'

const MOCK_GROUP_WEAKNESS_SUGGESTIONS: LeaderMetricSuggestion[] = [
  { variable: 'playerAskForHelp', suggestion: 'a daily “blocked?” check in stand-up.' },
  { variable: 'playerOverlapAirtimeTimesTotal', suggestion: 'let each speaker finish before responding.' },
  { variable: 'playerAskQuestionOpen', suggestion: 'start questions with “how” or “what”.' }
]

/** Where a 0–1 result turns middle and high in these fixtures. */
export const MOCK_SCORE_ZONE_FLOORS: LeaderScoreZoneFloors = { middleFrom: 0.4, highFrom: 0.6 }

/** Where a survey question's whole score out of five turns middle and high in these fixtures. */
export const MOCK_QUESTION_ZONE_FLOORS: LeaderScoreZoneFloors = { middleFrom: 3, highFrom: 4 }

const buildMockInsights = (overrides: Partial<LeaderInsightsView>): LeaderInsightsView => ({
  id: buildLeaderDocId(MOCK_ORGANIZATION_ID, MOCK_LEADER_USER_ID),
  organizationId: MOCK_ORGANIZATION_ID,
  leaderUserId: MOCK_LEADER_USER_ID,
  status: LeaderInsightsStatus.READY,
  generatedAt: nowIso(),
  scoreZoneFloors: MOCK_SCORE_ZONE_FLOORS,
  questionZoneFloors: MOCK_QUESTION_ZONE_FLOORS,
  peopleInScope: MOCK_LEADER_PEOPLE.length,
  peopleWithSessions: 0,
  peopleWithPersonality: 0,
  sessionCount: 0,
  groupStrengths: [],
  groupWeaknesses: [],
  groupPersonality: [],
  teams: [],
  ...overrides
})

export const MOCK_LEADER_INSIGHTS_READY: LeaderInsightsResult = {
  featureEnabled: true,
  isRefreshing: false,
  people: MOCK_LEADER_PEOPLE,
  insights: buildMockInsights({
    peopleWithSessions: MOCK_PSYCHOLOGICAL_SAFETY_SCORES.length,
    peopleWithPersonality: MOCK_LEADER_PEOPLE.length,
    sessionCount: 14,
    individualPsychologicalSafety: {
      peopleCount: MOCK_PSYCHOLOGICAL_SAFETY_SCORES.length,
      mean: roundScore(getMean(MOCK_PSYCHOLOGICAL_SAFETY_SCORES)),
      spread: buildScoreSpread(MOCK_PSYCHOLOGICAL_SAFETY_SCORES),
      questions: [
        { key: 'playerReflectionQuestionOne', mean: 0.74 },
        { key: 'playerReflectionQuestionTwo', mean: 0.66 },
        { key: 'playerReflectionQuestionThree', mean: 0.81 },
        { key: 'playerReflectionQuestionFour', mean: 0.58 },
        { key: 'playerReflectionQuestionFive', mean: 0.7 },
        { key: 'playerReflectionQuestionSix', mean: 0.63 },
        { key: 'playerReflectionQuestionSeven', mean: 0.77 }
      ]
    },
    groupStrengths: [
      {
        variable: 'playerOfferHelp',
        normalizedScore: 0.86,
        level: LeaderCollaborationLevel.HIGH,
        recommendationDirection: LeaderMetricDirection.INCREASE
      },
      {
        variable: 'playerInformReport',
        normalizedScore: 0.79,
        level: LeaderCollaborationLevel.HIGH,
        recommendationDirection: LeaderMetricDirection.INCREASE
      },
      {
        variable: 'playerAirtimeTotal',
        normalizedScore: 0.72,
        level: LeaderCollaborationLevel.ABOVE_HALF,
        recommendationDirection: LeaderMetricDirection.BALANCE
      }
    ],
    groupWeaknesses: [
      {
        variable: 'playerAskForHelp',
        normalizedScore: 0.31,
        level: LeaderCollaborationLevel.BELOW_HALF,
        recommendationDirection: LeaderMetricDirection.INCREASE
      },
      {
        variable: 'playerOverlapAirtimeTimesTotal',
        normalizedScore: 0.38,
        level: LeaderCollaborationLevel.BELOW_HALF,
        recommendationDirection: LeaderMetricDirection.DECREASE
      },
      {
        variable: 'playerAskQuestionOpen',
        normalizedScore: 0.44,
        level: LeaderCollaborationLevel.BELOW_HALF,
        recommendationDirection: LeaderMetricDirection.INCREASE
      }
    ],
    groupPersonality: [
      { key: 'openness', mean: 0.68 },
      { key: 'conscientiousness', mean: 0.74 },
      { key: 'extroversion', mean: 0.52 },
      { key: 'agreeableness', mean: 0.79 },
      { key: 'neuroticism', mean: 0.36 },
      { key: 'decisionMaking', mean: 0.61 },
      { key: 'helpingAssisting', mean: 0.83 },
      { key: 'communication', mean: 0.7 }
    ],
    teams: [
      {
        teamId: 'mock-team-north',
        teamName: MOCK_TEAM_NORTH_NAME,
        memberCount: MOCK_TEAM_NORTH_SIZE,
        sessionCount: 8,
        teamHealth: 0.76,
        psychologicalSafetyQuestions: [
          { key: 'teamReflectionQuestionOne', mean: 0.8 },
          { key: 'teamReflectionQuestionTwo', mean: 0.71 },
          { key: 'teamReflectionQuestionThree', mean: 0.77 },
          { key: 'teamReflectionQuestionFour', mean: 0.64 },
          { key: 'teamReflectionQuestionFive', mean: 0.73 },
          { key: 'teamReflectionQuestionSix', mean: 0.82 }
        ],
        strengths: [
          {
            variable: 'teamSharedResource',
            normalizedScore: 0.88,
            level: LeaderCollaborationLevel.HIGH,
            recommendationDirection: LeaderMetricDirection.INCREASE
          },
          {
            variable: 'teamTimesTalkedTotal',
            normalizedScore: 0.81,
            level: LeaderCollaborationLevel.HIGH,
            recommendationDirection: LeaderMetricDirection.INCREASE
          },
          {
            variable: 'teamAirtimeTotal',
            normalizedScore: 0.74,
            level: LeaderCollaborationLevel.ABOVE_HALF,
            recommendationDirection: LeaderMetricDirection.BALANCE
          }
        ],
        weaknesses: [
          {
            variable: 'teamSharedAbility',
            normalizedScore: 0.29,
            level: LeaderCollaborationLevel.BELOW_HALF,
            recommendationDirection: LeaderMetricDirection.INCREASE
          },
          {
            variable: 'teamAirtimeRelativePreplanning',
            normalizedScore: 0.37,
            level: LeaderCollaborationLevel.BELOW_HALF,
            recommendationDirection: LeaderMetricDirection.BALANCE
          },
          {
            variable: 'teamTimeAtMap',
            normalizedScore: 0.42,
            level: LeaderCollaborationLevel.BELOW_HALF,
            recommendationDirection: LeaderMetricDirection.BALANCE
          }
        ]
      },
      {
        teamId: 'mock-team-south',
        teamName: MOCK_TEAM_SOUTH_NAME,
        memberCount: 4,
        sessionCount: 6,
        teamHealth: 0.61,
        psychologicalSafetyQuestions: [
          { key: 'teamReflectionQuestionOne', mean: 0.62 },
          { key: 'teamReflectionQuestionTwo', mean: 0.58 },
          { key: 'teamReflectionQuestionThree', mean: 0.69 },
          { key: 'teamReflectionQuestionFour', mean: 0.55 },
          { key: 'teamReflectionQuestionFive', mean: 0.6 },
          { key: 'teamReflectionQuestionSix', mean: 0.66 }
        ],
        strengths: [
          {
            variable: 'teamSharedItem',
            normalizedScore: 0.75,
            level: LeaderCollaborationLevel.HIGH,
            recommendationDirection: LeaderMetricDirection.INCREASE
          },
          {
            variable: 'teamAirtimeExecution',
            normalizedScore: 0.69,
            level: LeaderCollaborationLevel.ABOVE_HALF,
            recommendationDirection: LeaderMetricDirection.BALANCE
          },
          {
            variable: 'teamTimesTalkedExecution',
            normalizedScore: 0.63,
            level: LeaderCollaborationLevel.ABOVE_HALF,
            recommendationDirection: LeaderMetricDirection.INCREASE
          }
        ],
        weaknesses: [
          {
            variable: 'teamTimesTalkedPreplanning',
            normalizedScore: 0.35,
            level: LeaderCollaborationLevel.BELOW_HALF,
            recommendationDirection: LeaderMetricDirection.INCREASE
          },
          {
            variable: 'teamSharedResource',
            normalizedScore: 0.4,
            level: LeaderCollaborationLevel.BELOW_HALF,
            recommendationDirection: LeaderMetricDirection.INCREASE
          },
          {
            variable: 'teamAirtimeEvaluation',
            normalizedScore: 0.46,
            level: LeaderCollaborationLevel.BELOW_HALF,
            recommendationDirection: LeaderMetricDirection.BALANCE
          }
        ]
      }
    ],
    leadershipHighlights: MOCK_LEADERSHIP_HIGHLIGHTS,
    leadershipSuggestion: MOCK_LEADERSHIP_SUGGESTION,
    groupWeaknessSuggestions: MOCK_GROUP_WEAKNESS_SUGGESTIONS,
    leadershipSummary: joinLeadershipNote(MOCK_LEADERSHIP_HIGHLIGHTS, MOCK_LEADERSHIP_SUGGESTION)
  })
}

export const MOCK_LEADER_INSIGHTS_NOT_ENOUGH_PEOPLE: LeaderInsightsResult = {
  featureEnabled: true,
  isRefreshing: false,
  people: MOCK_LEADER_PEOPLE.slice(0, LEADER_INSIGHTS_MIN_PEOPLE),
  insights: buildMockInsights({
    status: LeaderInsightsStatus.NOT_ENOUGH_PEOPLE,
    peopleInScope: LEADER_INSIGHTS_MIN_PEOPLE,
    peopleWithSessions: LEADER_INSIGHTS_MIN_PEOPLE - 1,
    peopleWithPersonality: 1,
    sessionCount: 2
  })
}

export const MOCK_LEADER_INSIGHTS_NO_DATA: LeaderInsightsResult = {
  featureEnabled: true,
  isRefreshing: false,
  people: MOCK_LEADER_PEOPLE,
  insights: buildMockInsights({ status: LeaderInsightsStatus.NO_DATA })
}

const MOCK_LEADER_INSIGHTS_BY_STATUS: Record<LeaderInsightsStatus, LeaderInsightsResult> = {
  [LeaderInsightsStatus.READY]: MOCK_LEADER_INSIGHTS_READY,
  [LeaderInsightsStatus.NOT_ENOUGH_PEOPLE]: MOCK_LEADER_INSIGHTS_NOT_ENOUGH_PEOPLE,
  [LeaderInsightsStatus.NO_DATA]: MOCK_LEADER_INSIGHTS_NO_DATA
}

/** Screenshot/dev override: pick a fixture by status, and patch any field of the result on top of it. */
export type LeaderInsightsMockOverride = Partial<LeaderInsightsResult> & { status?: LeaderInsightsStatus }

export const buildMockLeaderInsightsResult = (): LeaderInsightsResult => {
  const override =
    typeof window === 'undefined'
      ? undefined
      : (window as { __leaderInsightsMock?: LeaderInsightsMockOverride }).__leaderInsightsMock
  const { status = LeaderInsightsStatus.READY, ...resultPatch } = override ?? {}
  return { ...MOCK_LEADER_INSIGHTS_BY_STATUS[status], ...resultPatch }
}

export const MOCK_LEADER_SCOPE_MEMBERS: LeaderScopeMember[] = MOCK_LEADER_PEOPLE.map(({ displayName }, index) => ({
  userId: `mock-person-${index + 1}`,
  displayName,
  email: `${displayName.toLowerCase().replace(/[^a-z]+/g, '.')}@example.com`,
  teamIds: [index < MOCK_TEAM_NORTH_SIZE ? 'mock-team-north' : 'mock-team-south']
}))

export const buildMockLeaderScope = (params: {
  organizationId?: string
  leaderUserId: string
  userIds?: string[]
}): LeaderScope => {
  const organizationId = params.organizationId ?? MOCK_ORGANIZATION_ID
  return {
    id: buildLeaderDocId(organizationId, params.leaderUserId),
    organizationId,
    leaderUserId: params.leaderUserId,
    userIds: params.userIds ?? MOCK_LEADER_SCOPE_MEMBERS.map((member) => member.userId),
    createdAt: nowIso(),
    updatedAt: nowIso(),
    updatedByUserId: MOCK_LEADER_USER_ID
  }
}
